import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { getTypes, listActions } from '@plone/volto/actions';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import { useClient } from '@plone/volto/hooks/client/useClient';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';

import NotificacaoItem from './NotificacaoItem';
import MinhaFila from './MinhaFila';
import { useNotificacoes } from './useNotificacoes';
import type { FiltroNotificacoes } from 'volto-ministerio-publico-federal/helpers/Notificacoes';

const POR_PAGINA = 20;

const FILTROS: { id: FiltroNotificacoes; rotulo: string }[] = [
  { id: 'nao_lidas', rotulo: 'Não lidas' },
  { id: 'acao_necessaria', rotulo: 'Requerem ação' },
  { id: 'todas', rotulo: 'Todas' },
];

/**
 * Página `/notificacoes`: a caixa completa (filtros, paginação, arquivar) e a
 * fila de trabalho do fluxo editorial. Rota fora do conteúdo, por isso monta
 * a Toolbar via portal, como `MinhasPendencias`.
 */
const PaginaNotificacoes = () => {
  const { pathname } = useLocation();
  const isClient = useClient();
  const dispatch: any = useDispatch();
  const token = useSelector((state: any) => state.userSession?.token);
  const { estado, carregar, carregarFila, marcarLida, marcarTodas, arquivar } =
    useNotificacoes();
  const [filtro, setFiltro] = useState<FiltroNotificacoes>('nao_lidas');
  const [bStart, setBStart] = useState(0);

  // Ações e tipos da raiz para a Toolbar (a rota não é conteúdo).
  useEffect(() => {
    if (token) {
      dispatch(listActions(''));
      dispatch(getTypes(''));
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (token) carregar(filtro, bStart, POR_PAGINA);
  }, [token, filtro, bStart, carregar]);

  useEffect(() => {
    if (token) carregarFila();
  }, [token, carregarFila]);

  const { lista, fila, contagem } = estado;
  const total = lista.itemsTotal;
  const paginaAtual = Math.floor(bStart / POR_PAGINA) + 1;
  const paginas = Math.max(Math.ceil(total / POR_PAGINA), 1);

  const trocarFiltro = (novo: FiltroNotificacoes) => {
    setFiltro(novo);
    setBStart(0);
  };

  if (!token) {
    return (
      <div className="mpf-notificacoes-pagina mpf-notificacoes-pagina_container">
        <h1 className="mpf-notificacoes-pagina__titulo">Notificações</h1>
        <p>Entre no portal para ver suas notificações.</p>
      </div>
    );
  }

  // O contador é das pendências; as sugestões de publicação nacional são
  // opcionais e não contam.
  const pendentes = fila.items.filter((i) => !i.opcional).length;

  return (
    <div className="mpf-notificacoes-pagina mpf-notificacoes-pagina_container">
      <Helmet title="Notificações" />
      <div className="mpf-notificacoes-pagina__cabecalho">
        <h1 className="mpf-notificacoes-pagina__titulo">Notificações</h1>
        <div className="mpf-notificacoes-pagina__espaco" />
        <MPFButton
          variant="outline"
          size="m"
          disabled={contagem.nao_lidas === 0}
          onClick={() =>
            marcarTodas().then(() => carregar(filtro, bStart, POR_PAGINA))
          }
        >
          Marcar todas como lidas
        </MPFButton>
      </div>

      <section
        className="mpf-notificacoes-pagina__secao"
        aria-labelledby="mpf-caixa-titulo"
      >
        <h2
          id="mpf-caixa-titulo"
          className="mpf-notificacoes-pagina__subtitulo"
        >
          Caixa de notificações
        </h2>
        <div
          className="mpf-notificacoes-pagina__filtros"
          role="group"
          aria-label="Filtrar"
        >
          {FILTROS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={filtro === item.id}
              className={cx('mpf-notificacoes__aba', {
                'mpf-notificacoes__aba--ativa': filtro === item.id,
              })}
              onClick={() => trocarFiltro(item.id)}
            >
              {item.rotulo}
            </button>
          ))}
        </div>

        <div aria-live="polite" aria-busy={lista.loading}>
          {lista.error ? (
            <p className="mpf-notificacoes__vazio" role="alert">
              Não foi possível carregar as notificações.
            </p>
          ) : lista.loading && lista.items.length === 0 ? (
            <p className="mpf-notificacoes__vazio">Carregando…</p>
          ) : lista.items.length === 0 ? (
            <p className="mpf-notificacoes__vazio">
              Nenhuma notificação neste filtro.
            </p>
          ) : (
            <ul className="mpf-notificacoes__lista mpf-notificacoes__lista--pagina">
              {lista.items.map((item) => (
                <NotificacaoItem
                  key={item.id}
                  item={item}
                  compacto={false}
                  onAbrir={(n) => marcarLida(n, true)}
                  onAlternarLida={(n) => marcarLida(n, !n.lida)}
                  onArquivar={(n) => arquivar(n)}
                />
              ))}
            </ul>
          )}
        </div>

        {paginas > 1 && (
          <nav
            className="mpf-notificacoes-pagina__paginacao"
            aria-label="Paginação"
          >
            <MPFButton
              variant="outline"
              size="m"
              disabled={bStart === 0}
              onClick={() => setBStart(Math.max(bStart - POR_PAGINA, 0))}
            >
              Anteriores
            </MPFButton>
            <span>
              Página {paginaAtual} de {paginas}
            </span>
            <MPFButton
              variant="outline"
              size="m"
              disabled={paginaAtual >= paginas}
              onClick={() => setBStart(bStart + POR_PAGINA)}
            >
              Próximas
            </MPFButton>
          </nav>
        )}
      </section>

      <section
        className="mpf-notificacoes-pagina__secao"
        aria-labelledby="mpf-fila-titulo"
      >
        <h2 id="mpf-fila-titulo" className="mpf-notificacoes-pagina__subtitulo">
          Minha fila de trabalho
          {pendentes > 0 && (
            <span className="mpf-notificacoes__aba-contador">{pendentes}</span>
          )}
        </h2>
        <MinhaFila
          items={fila.items}
          loading={fila.loading}
          error={fila.error}
        />
      </section>

      {isClient &&
        createPortal(
          <Toolbar pathname={pathname} inner={<span />} />,
          document.getElementById('toolbar') as HTMLElement,
        )}
    </div>
  );
};

export default PaginaNotificacoes;
